import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { apiFetch, parseJsonSafely, toUserErrorMessage } from './api';
import ConfirmDialog from './ConfirmDialog';
import { canAccessRole, canAccessPage, getAppAuthRole } from './appAuth';
import { formatDateTimeDisplay } from './dateTime';
import { useGlobalErrorEffect } from './globalErrors';
import { Button, Modal, ModalHeader } from './ui';

function createChecklistItemId() {
  return `check-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function compareOrderNumbersAsc(leftOrder = {}, rightOrder = {}) {
  const leftNumber = String(leftOrder?.orderNumber || '').trim();
  const rightNumber = String(rightOrder?.orderNumber || '').trim();
  if (!leftNumber && !rightNumber) return 0;
  if (!leftNumber) return 1;
  if (!rightNumber) return -1;
  return leftNumber.localeCompare(rightNumber, 'ru', {
    numeric: true,
    sensitivity: 'base',
  });
}

function normalizeItemAttachments(attachments = []) {
  const sourceAttachments = Array.isArray(attachments) ? attachments : [];
  return sourceAttachments.reduce((acc, attachment) => {
    if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return acc;
    const attachmentId = String(attachment.attachmentId || '').trim();
    const name = String(attachment.name || '').trim();
    if (!attachmentId || !name) return acc;
    acc.push({
      attachmentId,
      name,
      type: String(attachment.type || '').trim(),
      size: Number(attachment.size) || 0,
      uploadedAt: String(attachment.uploadedAt || '').trim(),
      url: String(attachment.url || '').trim(),
    });
    return acc;
  }, []);
}

function normalizePackageItems(items = [], legacyPackageName = '') {
  const sourceItems = Array.isArray(items) ? items : [];
  const normalizedItems = sourceItems.reduce((acc, item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return acc;
    const name = String(item.name || '').trim();
    if (!name) return acc;
    acc.push({
      id: String(item.id || createChecklistItemId()).trim(),
      name,
      isCompleted: Boolean(item.isCompleted),
      completedAt: item.isCompleted ? (String(item.completedAt || '').trim() || new Date().toISOString().split('T')[0]) : null,
    });
    return acc;
  }, []);
  if (normalizedItems.length > 0) return normalizedItems;

  return String(legacyPackageName || '')
    .split(/[\n,;]+/g)
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const isCompleted = /^(\+|\[x\]|x\s+|✓\s+|✔\s+)/i.test(token);
      const normalizedName = token
        .replace(/^(\+|\-|\[x\]|\[\s\]|x\s+|✓\s+|✔\s+)/i, '')
        .trim();
      return {
        id: createChecklistItemId(),
        name: normalizedName || token,
        isCompleted,
        completedAt: isCompleted ? new Date().toISOString().split('T')[0] : null,
      };
    })
    .filter((item) => item.name);
}

function normalizeMaterialRequestItems(items = [], legacyRequests = '') {
  const sourceItems = Array.isArray(items) ? items : [];
  const normalizedItems = sourceItems.reduce((acc, item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return acc;
    const name = String(item.name || '').trim();
    if (!name) return acc;
    acc.push({
      id: String(item.id || createChecklistItemId()).trim(),
      name,
      kind: String(item.kind || (Array.isArray(item.attachments) && item.attachments.length > 0 ? 'photo' : 'text')).trim() || 'text',
      comment: String(item.comment || '').trim(),
      isCompleted: Boolean(item.isCompleted),
      completedAt: item.isCompleted ? (String(item.completedAt || '').trim() || new Date().toISOString().split('T')[0]) : null,
      attachments: normalizeItemAttachments(item.attachments),
    });
    return acc;
  }, []);
  if (normalizedItems.length > 0) return normalizedItems;
  return normalizePackageItems(items, legacyRequests).map((item) => ({
    ...item,
    kind: 'text',
    comment: '',
    attachments: [],
  }));
}

function getMaterialRequestItemDisplayName(item = {}) {
  const normalizedName = String(item.name || '').trim();
  if (normalizedName && normalizedName.toLowerCase() !== 'фото') return normalizedName;
  const legacyComment = String(item.comment || '').trim();
  if (legacyComment) return legacyComment;
  const firstAttachmentName = String(item.attachments?.[0]?.name || '').trim();
  if (firstAttachmentName) return firstAttachmentName;
  return normalizedName || 'Фото';
}

function getAttachmentOpenUrl(orderId, itemId, materialRequestItemId, attachment = {}) {
  if (String(attachment.url || '').trim()) {
    return String(attachment.url || '').trim();
  }
  const attachmentId = String(attachment.attachmentId || '').trim();
  if (!orderId || !itemId || !materialRequestItemId || !attachmentId) return '';
  return `/api/orders/${orderId}/items/${itemId}/material-request-items/${materialRequestItemId}/attachments/${attachmentId}/file`;
}

function getWorkshopRequestAttachmentOpenUrl(requestId, attachment = {}) {
  const attachmentId = String(attachment?.attachmentId || '').trim();
  if (!requestId || !attachmentId) return '';
  return `/api/workshop-requests/${requestId}/attachments/${attachmentId}/file`;
}

function getUnifiedSourceLabel(source = '') {
  if (source === 'package') return 'Комплектация';
  if (source === 'material') return 'Расходники';
  return 'Цех';
}

function buildExcelExportRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    '№ заявки': row.requestNumber ? `№ ${row.requestNumber}` : '',
    Источник: row.sourceLabel || '',
    Статус: row.status === 'completed' ? 'Выполнено' : 'Открыто',
    Заявка: row.text || '',
    Заказ: row.orderNumber ? `№ ${row.orderNumber}` : '',
    Заказчик: row.customer || '',
    Помещение: row.room || '',
    '№ изделия': row.itemNumber || '',
    Изделие: row.itemName || '',
    Автор: row.author || '',
    Дата: formatDateTimeDisplay(row.createdAt || row.updatedAt) || '',
    Фото: row.attachmentsCount > 0 ? `Да (${row.attachmentsCount})` : 'Нет',
  }));
}

function downloadMaterialRequestsWorkbook(rows = []) {
  const exportRows = buildExcelExportRows(rows);
  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  worksheet['!cols'] = [
    { wch: 16 },
    { wch: 12 },
    { wch: 42 },
    { wch: 12 },
    { wch: 22 },
    { wch: 18 },
    { wch: 12 },
    { wch: 24 },
    { wch: 20 },
    { wch: 20 },
    { wch: 12 },
  ];
  if (worksheet['!ref']) {
    worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Заявки');
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  XLSX.writeFile(workbook, `zayavki-na-materialy-${timestamp}.xlsx`);
}

function buildRequestRows(orders = [], workshopRequests = [], canManageRequests = true) {
  const workshopRows = (Array.isArray(workshopRequests) ? workshopRequests : []).map((request) => {
    const attachments = normalizeItemAttachments(request.attachments);
    const firstAttachment = attachments[0] || null;
    const requestNumber = Number(request.requestNumber) || 0;
    return {
      key: `workshop:${request._id}`,
      source: 'workshop',
      sourceLabel: getUnifiedSourceLabel('workshop'),
      status: String(request.status || '').trim() === 'completed' ? 'completed' : 'open',
      text: String(request.text || '').trim(),
      orderNumber: '',
      customer: '',
      room: '',
      itemNumber: '',
      itemName: '',
      author: String(request.employeeName || '').trim(),
      createdAt: String(request.createdAt || '').trim(),
      updatedAt: String(request.updatedAt || '').trim(),
      attachmentsCount: attachments.length,
      openUrl: firstAttachment ? getWorkshopRequestAttachmentOpenUrl(String(request._id || '').trim(), firstAttachment) : '',
      requestId: String(request._id || '').trim(),
      requestNumber,
      resolvedAt: String(request.resolvedAt || '').trim(),
      resolvedByName: String(request?.resolvedBy?.employeeName || '').trim(),
      toggleKind: 'workshop',
      canToggleStatus: Boolean(canManageRequests),
      canDelete: Boolean(canManageRequests),
      sortTimestamp: String(firstAttachment?.uploadedAt || request.createdAt || '').trim(),
    };
  });

  const orderRows = [...(Array.isArray(orders) ? orders : [])]
    .sort(compareOrderNumbersAsc)
    .flatMap((order) => {
      const orderItems = Array.isArray(order.items) ? order.items : [];
      return orderItems.flatMap((item) => {
        const packageRows = normalizePackageItems(item.packageItems, item.packageName).map((packageItem) => {
          const requestNumber = Number(packageItem.requestNumber) || 0;
          const createdAt = String(packageItem.createdAt || item.updatedAt || order.updatedAt || order.createdAt || '').trim();
          const resolvedAt = String(packageItem.completedAt || '').trim();
          const resolvedByName = String(packageItem?.completedBy?.employeeName || '').trim();
          const authorName = String(packageItem?.createdBy?.employeeName || '').trim();
          return ({
            key: `package:${order._id}:${item.itemId}:${packageItem.id}`,
            source: 'package',
            sourceLabel: getUnifiedSourceLabel('package'),
            status: packageItem.isCompleted ? 'completed' : 'open',
            text: packageItem.name,
            orderNumber: String(order.orderNumber || '').trim(),
            customer: String(order.customer || '').trim(),
            room: String(item.room || '').trim(),
            itemNumber: String(item.itemNumber || '').trim(),
            itemName: String(item.name || '').trim(),
            author: authorName,
            createdAt,
            updatedAt: String(item.updatedAt || order.updatedAt || order.createdAt || '').trim(),
            attachmentsCount: 0,
            openUrl: '',
            requestId: '',
            orderId: String(order._id || '').trim(),
            itemId: String(item.itemId || '').trim(),
            packageItemId: String(packageItem.id || '').trim(),
            requestNumber,
            resolvedAt,
            resolvedByName,
            toggleKind: 'package',
            canToggleStatus: Boolean(canManageRequests),
            canDelete: Boolean(canManageRequests),
            sortTimestamp: String(packageItem.createdAt || order.createdAt || '').trim(),
          });
        });

        const materialRows = normalizeMaterialRequestItems(item.materialRequestItems, item.materialRequests).map((requestItem) => {
          const firstAttachment = Array.isArray(requestItem.attachments) ? requestItem.attachments[0] : null;
          const requestNumber = Number(requestItem.requestNumber) || 0;
          const createdAt = String(requestItem.createdAt || firstAttachment?.uploadedAt || item.updatedAt || order.updatedAt || order.createdAt || '').trim();
          const resolvedAt = String(requestItem.completedAt || '').trim();
          const resolvedByName = String(requestItem?.completedBy?.employeeName || '').trim();
          const authorName = String(requestItem?.createdBy?.employeeName || '').trim();
          return {
            key: `material:${order._id}:${item.itemId}:${requestItem.id}`,
            source: 'material',
            sourceLabel: getUnifiedSourceLabel('material'),
            status: requestItem.isCompleted ? 'completed' : 'open',
            text: getMaterialRequestItemDisplayName(requestItem),
            orderNumber: String(order.orderNumber || '').trim(),
            customer: String(order.customer || '').trim(),
            room: String(item.room || '').trim(),
            itemNumber: String(item.itemNumber || '').trim(),
            itemName: String(item.name || '').trim(),
            author: authorName,
            createdAt,
            updatedAt: String(item.updatedAt || order.updatedAt || order.createdAt || '').trim(),
            attachmentsCount: Array.isArray(requestItem.attachments) ? requestItem.attachments.length : 0,
            openUrl: firstAttachment ? getAttachmentOpenUrl(order._id, item.itemId, requestItem.id, firstAttachment) : '',
            requestId: '',
            orderId: String(order._id || '').trim(),
            itemId: String(item.itemId || '').trim(),
            materialRequestItemId: String(requestItem.id || '').trim(),
            requestNumber,
            resolvedAt,
            resolvedByName,
            toggleKind: 'material',
            canToggleStatus: Boolean(canManageRequests),
            canDelete: Boolean(canManageRequests),
            sortTimestamp: String(requestItem.createdAt || firstAttachment?.uploadedAt || order.createdAt || '').trim(),
          };
        });

        return [...packageRows, ...materialRows];
      });
    });

  return [...workshopRows, ...orderRows].sort((left, right) => {
    const leftTime = new Date(left.sortTimestamp || 0).getTime();
    const rightTime = new Date(right.sortTimestamp || 0).getTime();
    if (rightTime !== leftTime) return rightTime - leftTime;
    return String(left.key || '').localeCompare(String(right.key || ''), 'ru', {
      numeric: true,
      sensitivity: 'base',
    });
  });
}

function WorkshopRequestsPage() {
  const authRole = getAppAuthRole();
  const canManageRequests = canAccessRole('manager', authRole)
    || canAccessPage('orders')
    || canAccessPage('requests');
  const [orders, setOrders] = useState([]);
  const [workshopRequests, setWorkshopRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [orderFilter, setOrderFilter] = useState('all');
  const [updatingKey, setUpdatingKey] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [completedKeyOrder, setCompletedKeyOrder] = useState([]);

  useGlobalErrorEffect(error, 'Ошибка раздела заявок.');

  const fetchData = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const [ordersRes, requestsRes] = await Promise.all([
        apiFetch('/api/orders'),
        apiFetch('/api/workshop-requests'),
      ]);
      const [ordersData, requestsData] = await Promise.all([
        parseJsonSafely(ordersRes),
        parseJsonSafely(requestsRes),
      ]);
      if (!ordersRes.ok) {
        throw new Error(ordersData?.message || 'Не удалось загрузить заказы.');
      }
      if (!requestsRes.ok) {
        throw new Error(requestsData?.message || 'Не удалось загрузить цеховые заявки.');
      }
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setWorkshopRequests(Array.isArray(requestsData?.items) ? requestsData.items : []);
    } catch (loadError) {
      setError(toUserErrorMessage(loadError, 'Не удалось загрузить сводную таблицу заявок.'));
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData({ showLoader: true });
  }, [fetchData]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      if (updatingKey) return;
      fetchData({ showLoader: false });
    };

    const intervalId = window.setInterval(refresh, 3000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [fetchData, updatingKey]);

  useEffect(() => () => {
    if (attachmentPreview?.url) {
      window.URL.revokeObjectURL(attachmentPreview.url);
    }
  }, [attachmentPreview]);

  const rows = useMemo(
    () => buildRequestRows(orders, workshopRequests, canManageRequests),
    [orders, workshopRequests, canManageRequests],
  );

  const authorOptions = useMemo(() => Array.from(new Set(
    rows.map((row) => String(row.author || '').trim()).filter(Boolean),
  )).sort((left, right) => left.localeCompare(right, 'ru', { sensitivity: 'base' })), [rows]);

  const orderOptions = useMemo(() => Array.from(new Set(
    rows.map((row) => String(row.orderNumber || '').trim()).filter(Boolean),
  )).sort((left, right) => left.localeCompare(right, 'ru', { numeric: true, sensitivity: 'base' })), [rows]);

  const filteredRows = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    const numericQueryOnly = String(search || '').trim().replace(/\D/g, '');
    const sourceRows = rows.filter((row) => {
      if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
      if (authorFilter !== 'all' && row.author !== authorFilter) return false;
      if (orderFilter !== 'all' && row.orderNumber !== orderFilter) return false;
      if (!query) return true;
      const haystack = [
        row.text,
        row.orderNumber,
        row.customer,
        row.room,
        row.itemNumber,
        row.itemName,
        row.author,
        row.sourceLabel,
        row.requestNumber ? String(row.requestNumber) : '',
      ].join(' ').toLowerCase();
      if (haystack.includes(query)) return true;
      if (numericQueryOnly) {
        const numberHaystack = [
          String(row.requestNumber || ''),
          String(row.orderNumber || ''),
          String(row.itemNumber || ''),
        ].join(' ');
        if (numberHaystack.includes(numericQueryOnly)) return true;
      }
      return false;
    });

    const completedSet = new Set((Array.isArray(completedKeyOrder) ? completedKeyOrder : []).filter(Boolean));
    const completedKeyIndex = new Map();
    completedSet.forEach((k, i) => completedKeyIndex.set(k, i));

    const openRows = [];
    const completedRowsInToggleOrder = [];
    const completedRowsByCreation = [];
    for (const row of sourceRows) {
      const isCompleted = row.status === 'completed';
      if (!isCompleted) {
        openRows.push(row);
      } else if (completedKeyIndex.has(row.key)) {
        completedRowsInToggleOrder.push({
          row,
          order: completedKeyIndex.get(row.key),
        });
      } else {
        completedRowsByCreation.push(row);
      }
    }
    completedRowsInToggleOrder.sort((left, right) => left.order - right.order);
    return [
      ...openRows,
      ...completedRowsInToggleOrder.map((entry) => entry.row),
      ...completedRowsByCreation,
    ];
  }, [authorFilter, completedKeyOrder, orderFilter, rows, search, sourceFilter]);

  const handleDownloadExcel = useCallback(() => {
    setError('');
    try {
      downloadMaterialRequestsWorkbook(filteredRows);
    } catch (downloadError) {
      setError(toUserErrorMessage(downloadError, 'Не удалось скачать Excel-файл заявок.'));
    }
  }, [filteredRows]);

  const handleToggleRequestStatus = useCallback(async (row) => {
    if (!row?.canToggleStatus) return;
    const nextStatus = row.status === 'completed' ? 'open' : 'completed';
    setUpdatingKey(row.key);
    setError('');
    try {
      let res = null;
      if (row.toggleKind === 'workshop') {
        res = await apiFetch(`/api/workshop-requests/${row.requestId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: nextStatus }),
        });
      } else if (row.toggleKind === 'material') {
        res = await apiFetch(`/api/orders/${row.orderId}/material-request-items/${row.materialRequestItemId}/toggle`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ itemId: row.itemId }),
        });
      } else if (row.toggleKind === 'package') {
        res = await apiFetch(`/api/orders/${row.orderId}/package-items/${row.packageItemId}/toggle`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ itemId: row.itemId }),
        });
      } else {
        return;
      }
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось изменить статус заявки.');
      }
      if (nextStatus === 'completed') {
        setCompletedKeyOrder((current) => {
          const prev = Array.isArray(current) ? current.slice() : [];
          const set = new Set(prev.filter((x) => x && x !== row.key));
          return [...set, row.key];
        });
      } else if (nextStatus === 'open') {
        setCompletedKeyOrder((current) => {
          const prev = Array.isArray(current) ? current : [];
          return prev.filter((x) => x && x !== row.key);
        });
      }
      await fetchData({ showLoader: false });
    } catch (toggleError) {
      setError(toUserErrorMessage(toggleError, 'Не удалось изменить статус заявки.'));
    } finally {
      setUpdatingKey('');
    }
  }, [fetchData]);

  const handleDeleteRequest = useCallback(async () => {
    if (!confirmDelete?.row) return;
    const row = confirmDelete.row;
    setUpdatingKey(row.key);
    setError('');
    try {
      let res = null;
      if (row.source === 'workshop') {
        res = await apiFetch(`/api/workshop-requests/${row.requestId}`, {
          method: 'DELETE',
        });
      } else if (row.source === 'material') {
        res = await apiFetch(`/api/orders/${row.orderId}/material-request-items/${row.materialRequestItemId}?itemId=${encodeURIComponent(row.itemId)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ itemId: row.itemId }),
        });
      } else if (row.source === 'package') {
        res = await apiFetch(`/api/orders/${row.orderId}/package-items/${row.packageItemId}?itemId=${encodeURIComponent(row.itemId)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ itemId: row.itemId }),
        });
      } else {
        return;
      }

      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось удалить заявку.');
      }

      setConfirmDelete(null);
      await fetchData({ showLoader: false });
    } catch (deleteError) {
      setError(toUserErrorMessage(deleteError, 'Не удалось удалить заявку.'));
    } finally {
      setUpdatingKey('');
    }
  }, [confirmDelete, fetchData]);

  const closeAttachmentPreview = useCallback(() => {
    setAttachmentPreview((current) => {
      if (current?.url) {
        window.URL.revokeObjectURL(current.url);
      }
      return null;
    });
  }, []);

  const handleOpenRequestAttachmentPreview = useCallback(async (row) => {
    if (!row?.openUrl) return;
    setUpdatingKey(row.key);
    setError('');
    try {
      const res = await apiFetch(row.openUrl);
      if (!res.ok) {
        const data = await parseJsonSafely(res);
        throw new Error(data?.message || 'Не удалось открыть фото заявки.');
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setAttachmentPreview((current) => {
        if (current?.url) {
          window.URL.revokeObjectURL(current.url);
        }
        return {
          key: row.key,
          name: row.text || 'Фото заявки',
          kindLabel: 'Изображение',
          url: blobUrl,
        };
      });
    } catch (openError) {
      setError(toUserErrorMessage(openError, 'Не удалось открыть фото заявки.'));
    } finally {
      setUpdatingKey('');
    }
  }, []);

  return (
    <div className="workshop-requests-page">
      <div className="card orders-workspace-table-card">
        <div className="section-header">
          <div>
            <h2 style={{ margin: 0 }}>Заявки на материалы</h2>
            <div className="filters-summary">
              Общий реестр цеховых заявок, комплектации заказа и заявок на расходники.
            </div>
          </div>
          <div className="table-action-group">
            <Button
              variant="success"
              className="section-toolbar-btn"
              onClick={handleDownloadExcel}
              disabled={loading || Boolean(updatingKey) || filteredRows.length === 0}
            >
              Скачать Excel
            </Button>
          </div>
        </div>

        <div className="responsive-filters workshop-requests-filters">
          <label className="workshop-filter-field">
            <span className="workshop-filter-label">Поиск</span>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Заявка, заказ, изделие, автор"
            />
          </label>
          <label className="workshop-filter-field">
            <span className="workshop-filter-label">Источник</span>
            <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
              <option value="all">Все</option>
              <option value="workshop">Цех</option>
              <option value="package">Комплектация</option>
              <option value="material">Расходники</option>
            </select>
          </label>
          <label className="workshop-filter-field">
            <span className="workshop-filter-label">Автор</span>
            <select value={authorFilter} onChange={(event) => setAuthorFilter(event.target.value)}>
              <option value="all">Все</option>
              {authorOptions.map((author) => (
                <option key={author} value={author}>{author}</option>
              ))}
            </select>
          </label>
          <label className="workshop-filter-field">
            <span className="workshop-filter-label">Заказ</span>
            <select value={orderFilter} onChange={(event) => setOrderFilter(event.target.value)}>
              <option value="all">Все</option>
              {orderOptions.map((orderNumber) => (
                <option key={orderNumber} value={orderNumber}>{orderNumber}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="filters-summary" style={{ marginTop: 8 }}>
          Показано строк: {filteredRows.length}
        </div>

        {error ? (
          <div className="settings-alert settings-alert-error" style={{ marginTop: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="table-scroll" style={{ marginTop: 16 }}>
          <table className="unified-orders-table workshop-requests-table">
            <colgroup>
              <col style={{ width: 64, minWidth: 64, maxWidth: 64 }} />
              <col style={{ width: 96, minWidth: 96, maxWidth: 96 }} />
              <col style={{ width: 56, minWidth: 56, maxWidth: 56 }} />
              <col />
              <col />
              <col />
              <col />
              <col style={{ width: 116, minWidth: 116, maxWidth: 116 }} />
              <col style={{ width: 72, minWidth: 72, maxWidth: 72 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ width: 64 }}>№</th>
                <th style={{ width: 96 }}>Источник</th>
                <th style={{ width: 56 }}>Чек</th>
                <th>Заявка</th>
                <th>Заказ</th>
                <th>Помещение / изделие</th>
                <th>Автор</th>
                <th style={{ width: 116 }}>Дата</th>
                <th style={{ width: 72 }}>Удалить</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="order-card-empty">По текущим фильтрам заявки не найдены.</div>
                  </td>
                </tr>
              ) : null}
              {filteredRows.map((row) => {
                const isUpdating = updatingKey === row.key;
                const isCompleted = row.status === 'completed';
                const rowClass = [
                  isCompleted ? 'workshop-requests-row--completed' : '',
                ].filter(Boolean).join(' ').trim();
                return (
                  <tr
                    key={row.key}
                    className={rowClass || undefined}
                  >
                    <td>
                      <span className="workshop-request-number">{row.requestNumber ? `№ ${row.requestNumber}` : '—'}</span>
                    </td>
                    <td>
                      <span className="workshop-source-text">
                        {row.sourceLabel}
                      </span>
                    </td>
                    <td>
                      {row.canToggleStatus ? (
                        <label className="workshop-check-cell">
                          <input
                            type="checkbox"
                            checked={row.status === 'completed'}
                            onChange={() => handleToggleRequestStatus(row)}
                            disabled={isUpdating}
                          />
                          <span>{isUpdating ? '...' : ''}</span>
                        </label>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div className="workshop-request-main">
                        {row.openUrl ? (
                          <span className="workshop-request-link-wrap workshop-request-link-wrap--with-photo">
                            <button
                              type="button"
                              className="workshop-request-link workshop-request-link--with-photo"
                              onClick={() => handleOpenRequestAttachmentPreview(row)}
                              disabled={isUpdating}
                              title="Нажмите, чтобы посмотреть фото заявки"
                            >
                              <span className="workshop-request-link-photo-icon" aria-hidden="true">📷</span>
                              <span className="workshop-request-link-text">{row.text}</span>
                            </button>
                            <span className="workshop-request-link-indicator workshop-request-link-indicator--strong" aria-hidden="true">
                              Фото
                            </span>
                          </span>
                        ) : (
                          <span>{row.text}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {row.orderNumber ? (
                        <div className="workshop-cell-stack">
                          <strong>№ {row.orderNumber}</strong>
                          <span>{row.customer || 'Без заказчика'}</span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {row.itemName ? (
                        <div className="workshop-cell-stack">
                          <strong>{row.itemName}</strong>
                          <span>{[row.room, row.itemNumber ? `изд. ${row.itemNumber}` : ''].filter(Boolean).join(' • ') || '—'}</span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{row.author || '—'}</td>
                    <td>{formatDateTimeDisplay(row.createdAt || row.updatedAt) || '—'}</td>
                    <td>
                      {row.canDelete ? (
                        <button
                          type="button"
                          className="workshop-delete-btn"
                          onClick={() => setConfirmDelete({
                            row,
                            title: 'Удалить заявку?',
                            message: row.source === 'package'
                              ? `Позиция комплектации "${row.text}" будет удалена без возможности восстановления.`
                              : `Заявка "${row.text}" будет удалена без возможности восстановления.`,
                            confirmLabel: 'Удалить',
                          })}
                          disabled={isUpdating}
                          aria-label={`Удалить ${row.text}`}
                        >
                          🗑
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={confirmDelete?.title || 'Удалить заявку?'}
        message={confirmDelete?.message || ''}
        confirmLabel={confirmDelete?.confirmLabel || 'Удалить'}
        onConfirm={handleDeleteRequest}
        onCancel={() => !updatingKey && setConfirmDelete(null)}
        loading={Boolean(confirmDelete && updatingKey === confirmDelete.row?.key)}
        variant="danger"
      />
      {attachmentPreview ? (
        <Modal open={Boolean(attachmentPreview)} onClose={closeAttachmentPreview} size="xl" className="order-form-modal">
          <ModalHeader
            title={attachmentPreview.name || 'Просмотр фото'}
            subtitle={attachmentPreview.kindLabel || 'Изображение'}
            onClose={closeAttachmentPreview}
          />
          <div className="attachment-preview-panel">
            <div className="attachment-preview-toolbar">
              <div className="attachment-preview-toolbar-meta">
                <span className="attachment-preview-toolbar-kind">{attachmentPreview.kindLabel || 'Файл'}</span>
              </div>
            </div>
            <div className="attachment-preview-wrap attachment-preview-wrap-image">
              <img src={attachmentPreview.url} alt={attachmentPreview.name || 'Изображение'} className="attachment-preview-image" />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default WorkshopRequestsPage;
